import { memo, useCallback, useState, useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { ItemTree, useKeyboardNavigation } from "@repo/shared-components";
import { cn } from "@repo/utils";
import { Title } from "~/components/ui/title";
import { TaskExplorerToolbar } from "./toolbar/task-explorer-toolbar";
import { useTaskSorting } from "../../hooks/use-task-sorting";
import { useTabWindowsContext } from "../../tab-windows/hooks/use-tab-windows-context";
import {
  SortBy,
  type SortType,
  type TabWindow,
  type ExplorerItem,
  type StatusGroup,
} from "../../task.types";
import { ChangeStatusPanel } from "../panel/change-status/change-status-panel";
import { ListsPanel } from "../panel/list/lists-panel";
import { PANEL_BUTTONS, TaskPanel, type PanelButton } from "../panel/task-panel";

type Props = {
  window: TabWindow<StatusGroup>;
};

export const TaskExplorer = memo<Props>(({ window }: Props) => {
  const { user } = useAuth();
  const currentUserId = user?.profile?.sub ?? null;
  const {
    activePanelWindowId,
    closeWindow,
    updateWindowState,
    changeWindowOrder,
    handleItemSelection,
    togglePanelForWindow,
    updateDetailViewForWindow,
  } = useTabWindowsContext<ExplorerItem>();

  const [activePanelButtons, setActivePanelButtons] = useState<Set<PanelButton>>(new Set());
  const [showChangeStatusPanel, setShowChangeStatusPanel] = useState(false);
  const [showListsPanel, setShowListsPanel] = useState(false);

  // Default to Ascending direction
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [anchorId, setAnchorId] = useState<number | null>(null);

  // Default to Workflow if sortType is undefined
  const currentSortType = window.state.sortType ?? SortBy.Workflow;

  const { sortedGroups, displayItems } = useTaskSorting(
    window.items as StatusGroup[],
    currentSortType,
    sortDirection
  );

  const isPanelOn = activePanelWindowId === window.id;

  const totalLeafCount = useMemo(() => {
    let count = 0;

    const countLeafItems = (nodes: ExplorerItem[]) => {
      for (const node of nodes) {
        if ("taskId" in node && "statusCode" in node) {
          count++;
        }
        if ("children" in node && Array.isArray(node.children)) {
          countLeafItems(node.children as ExplorerItem[]);
        }
      }
    };

    countLeafItems(window.items as ExplorerItem[]);
    return count;
  }, [window.items]);

  const disabledItemIds = useMemo(() => {
    const ids = new Set<number>();
    const storeDisabled = (nodes: ExplorerItem[]) => {
      for (const node of nodes) {
        if ("taskId" in node && "statusCode" in node) {
          const lock = node.lock as { userId: string } | null | undefined;
          if (lock && (!currentUserId || lock.userId !== currentUserId)) {
            ids.add(node.id);
          }
        }
        if ("children" in node && Array.isArray(node.children)) {
          storeDisabled(node.children as ExplorerItem[]);
        }
      }
    };
    storeDisabled(window.items as ExplorerItem[]);
    return ids;
  }, [window.items, currentUserId]);

  const handleKeyboardNavigate = useCallback(
    (itemId: number, selectedItems: Set<number>) => {
      updateWindowState(window.id, { ...window.state, selectedItems });

      if (selectedItems.size === 0) return;
      updateDetailViewForWindow(window.id, selectedItems, true);
    },
    [window.id, window.state, updateWindowState, updateDetailViewForWindow]
  );

  const { containerRef, setFocusedItem, getVisibleItemIds } = useKeyboardNavigation({
    enabled: !window.state.isMinimized,
    windowId: window.id,
    onNavigate: handleKeyboardNavigate,
  });

  const handleItemClick = (item: ExplorerItem, e: React.MouseEvent) => {
    const isMulti = e.ctrlKey || e.metaKey;
    const isShift = e.shiftKey;
    const itemId = (item as unknown as { id: number }).id;

    if (disabledItemIds.has(itemId)) return;

    setFocusedItem(itemId);

    if (isShift && anchorId !== null) {
      const visibleIds = getVisibleItemIds();
      const startIndex = visibleIds.indexOf(anchorId);
      const endIndex = visibleIds.indexOf(itemId);

      if (startIndex !== -1 && endIndex !== -1) {
        const [start, end] = [startIndex, endIndex].sort((a, b) => a - b);
        const range = new Set(visibleIds.slice(start, (end ?? 0) + 1));
        handleItemSelection(window.id, range, false, isPanelOn);
      }
    } else {
      setAnchorId(itemId);
      handleItemSelection(window.id, itemId, isMulti, isPanelOn);
    }
  };

  const handleActivePanelButton = (button: PanelButton) => {
    setActivePanelButtons((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(button)) {
        newSet.delete(button);
      } else {
        newSet.add(button);
      }
      return newSet;
    });
  };

  const handleToggleItemPanel = () => {
    togglePanelForWindow(window.id);
  };

  const handleSortChange = useCallback(
    (sortType: SortType | undefined, direction: "asc" | "desc" = "asc") => {
      setSortDirection(direction);
      updateWindowState(window.id, { ...window.state, sortType });
    },
    [window.id, window.state, updateWindowState]
  );

  const handleMinimize = () => {
    updateWindowState(window.id, { ...window.state, isMinimized: !window.state.isMinimized });
  };

  const handlePin = () => {
    updateWindowState(window.id, { ...window.state, isPinned: !window.state.isPinned });
  };

  const handleClose = () => {
    closeWindow(window.id);
  };

  const handleMoveLeft = () => {
    changeWindowOrder(window.id, "left");
  };

  const handleMoveRight = () => {
    changeWindowOrder(window.id, "right");
  };

  const handleToggleChangeStatusPanel = () => {
    handleActivePanelButton(PANEL_BUTTONS.ChangeStatus);
    setShowChangeStatusPanel((prev) => !prev);
  };

  const handleToggleListsPanel = () => {
    handleActivePanelButton(PANEL_BUTTONS.List);
    setShowListsPanel((prev) => !prev);
  };

  return (
    <div
      className={cn(
        "border-gxe-border flex h-full w-full max-w-96 flex-col border-r transition-all duration-200",
        {
          "w-12": window.state.isMinimized,
          "ring-opacity-30 ring-gxe-primary ml-0.5 ring-2": window.state.isPinned,
        }
      )}
    >
      <TaskExplorerToolbar
        currentState={window.state}
        onClose={handleClose}
        onMinimize={handleMinimize}
        isPanelOn={isPanelOn}
        onPin={handlePin}
        onToggleItemPanel={handleToggleItemPanel}
        onMoveLeft={handleMoveLeft}
        onMoveRight={handleMoveRight}
      />

      {!window.state.isMinimized && (
        <div className="flex min-h-0 flex-1 flex-col">
          <Title
            headerClassName="border-b border-gray-200"
            textClassName={cn({ "text-gxe-primary": window.state.isPinned })}
            text={window.state.title ?? "File Explorer"}
            subtitle={window.state.subtitle}
            totalCount={totalLeafCount}
          />
          <TaskPanel
            sortBy={currentSortType}
            sortDirection={sortDirection}
            activeButtons={activePanelButtons}
            onSortChange={handleSortChange}
            onChangeStatusClick={handleToggleChangeStatusPanel}
            onListsClick={handleToggleListsPanel}
          />
          {showChangeStatusPanel && (
            <ChangeStatusPanel
              sortedItems={sortedGroups}
              selectedExplorerItems={window.state.selectedItems ?? new Set()}
              taskIds={window.state.taskIds}
              onClose={() => {
                handleActivePanelButton(PANEL_BUTTONS.ChangeStatus);
                setShowChangeStatusPanel(false);
              }}
            />
          )}

          {showListsPanel && (
            <ListsPanel
              title={window.state.title}
              sortedItems={sortedGroups}
              windowId={window.id}
              onClose={() => {
                handleActivePanelButton(PANEL_BUTTONS.List);
                setShowListsPanel(false);
              }}
            />
          )}

          <div
            ref={containerRef}
            tabIndex={0}
            className="min-h-0 flex-1 overflow-y-auto p-2 focus:outline-none"
          >
            <ItemTree
              items={displayItems}
              level={0}
              onItemClick={handleItemClick}
              selectedItems={window.state.selectedItems || new Set()}
              disabledItemIds={disabledItemIds}
              windowId={window.id}
              showGroupId
            />
          </div>
        </div>
      )}

      {window.state.isMinimized && (
        <div className="flex h-full items-center justify-center">
          <div className="rotate-90 text-xs font-medium text-gray-600">{window.state.title}</div>
        </div>
      )}
    </div>
  );
});

TaskExplorer.displayName = "TaskExplorer";
